import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, startWith } from 'rxjs/operators';

import { ProductionSettingsComponent } from '../../components/production-settings/production-settings.component';
import { AttentionLimits } from '../../models/attention-limits.model';
import { ProductionState } from '../../models/production.model';
import { AttentionLimitsService } from '../../services/attention-limits.service';
import * as AttentionLimitsActions from '../actions/attention-limits.actions';

@Injectable()
export class AttentionLimitsEffects {
  private readonly actions$ = inject(Actions);
  private readonly attentionLimitsService = inject(AttentionLimitsService);
  private readonly dialogService = inject(DialogService);
  private readonly localeService = inject(LocaleService);
  private readonly store = inject<Store<ProductionState>>(Store);

  protected editProductionSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttentionLimitsActions.editAttentionLimits),
      mergeMap(() =>
        this.attentionLimitsService.get().pipe(
          map((attentionLimits: AttentionLimits) => {
            this.dialogService
              .form(ProductionSettingsComponent, {})
              .pipe(filter(({ status }) => status === DIALOG_RETURN_TYPES.confirm))
              .subscribe(({ result }) => {
                this.store.dispatch(
                  AttentionLimitsActions.saveAttentionLimits({
                    attentionLimits: result.attentionLimits,
                  }),
                );
              });
            return AttentionLimitsActions.loadAttentionLimitsSuccess({ attentionLimits });
          }),
          catchError((errorResponse) => {
            const message = errorResponse.error?.message
              ? this.localeService.translate(errorResponse.error.message)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  protected getProductionSettingsByDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttentionLimitsActions.loadAttentionLimitsRequest),
      mergeMap((values) =>
        this.attentionLimitsService.getByDate(values.date).pipe(
          map((attentionLimits: AttentionLimits) =>
            AttentionLimitsActions.loadAttentionLimitsByDateSuccess({
              date: values.date,
              attentionLimits,
            }),
          ),
        ),
      ),
    ),
  );

  protected saveAttentionLimits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttentionLimitsActions.saveAttentionLimits),
      map((action) => action.attentionLimits),
      mergeMap((attentionLimits) =>
        this.attentionLimitsService.post(attentionLimits).pipe(
          mergeMap(() => of(AttentionLimitsActions.saveAttentionLimitsSucceedeed())),
          catchError((errorResponse) => {
            const message = errorResponse.error
              ? this.localeService.translate(errorResponse.error)
              : errorResponse.message;
            return of(NotificationActions.errorSnackbar({ message }));
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
